import React from "react";
import {useFormik} from "formik";
import {useStore} from "hooks/useStore";
import {useState} from "react";
import {newCourierValidation} from "../newCourierValidation";
import {useTranslation} from "react-i18next";
import moment from "moment";
import {newCourierFormPropType} from "../newCourierPropTypes";
import {useHistory} from "react-router-dom/cjs/react-router-dom.min";
import {
    FormInput,
    FormSelect,
    MaskedInputField,
    TextAreaField,
    FormCheckbox,
    FormDatePicker,
    FormSwitch
} from "../formFields/FormFields";
import {formatLicensePlate} from "utils/helpers";

export const useNewCourier = ({courier, courierId, setLoadingCourier}) => {
    const stores = useStore();
    const history = useHistory();
    const {t, i18n} = useTranslation(["newCourier"]);
    const [geolocationLoading, setGeolocationLoading] = useState(false);
    const [mapZoom, setMapZoom] = useState(15);
    const [mapViewCenter, setMapViewCenter] = useState(null);
    const [courierPhoto, setCourierPhoto] = useState("");

    const extractNameAndSurname = courier => {
        const courierData = courier?.data;
        const splitted = courierData?.fullname?.trim()?.split(" ") || [];
        const name =
            courierData?.name || splitted?.length > 1
                ? splitted?.slice(0, -1).join(" ")
                : splitted?.[0] || "";
        const surname =
            courierData?.surname || (splitted?.length > 1 ? splitted?.[splitted?.length - 1] : "");
        return {name, surname};
    };

    const formik = useFormik({
        initialValues: {
            fullname: "",
            name: extractNameAndSurname(courier)?.name || "",
            surname: extractNameAndSurname(courier)?.surname || "",
            type: "",
            cellPhone: "",
            email: "",
            birthDate: "",
            tckn: "",
            bloodGroup: "",
            address: "",
            vehicleInfo: "",
            licensePlate: "",
            emergencyContactName: "",
            emergencyContactPhone: "",
            emergencyContactDegree: "",
            city: "",
            county: "",
            bankName: "",
            iban: "",
            taxNumber: "",
            trained: false,
            isTestUser: false,
            photo: "",
            gender: "",
            preferredCityId: "",
            preferredDistrictIds: [],
            ossNumber: undefined,
            ossStartDate: undefined,
            mhNumber: undefined,
            vehicleOwnership: undefined,
            hasBagRegistered: undefined,
            hasProfessionalCompetenceCertificate: undefined,
            employmentType: undefined,
            workingModelId: undefined,
            managerCompany: undefined,
            homeLocation: {
                longitude: 0,
                latitude: 0
            },
            ...(courier && courier.data),
            // Backend tarihi providerCompanyTransitionDate olarak saklıyor; forma vionTransitionDate ile bağlanır
            vionTransitionDate:
                (courier && courier.data && courier.data.providerCompanyTransitionDate) || undefined
        },
        validationSchema: newCourierValidation,
        enableReinitialize: true,
        onSubmit: async (values: newCourierFormPropType) => {
            const checkCourierDedicationRequiredFieldsResult = checkCourierDedicationRequiredFields(
                {
                    courier: values,
                    companyId: courier?.data?.dedicatedCompanyId?.toString()
                }
            );
            if (!checkCourierDedicationRequiredFieldsResult) {
                return;
            }

            await setLoadingCourier(true);
            values.birthDate = moment(values.birthDate).format("YYYY-MM-DD");
            values.cellPhone = values.cellPhone.replace(/[-,(,)]/g, "");
            values.emergencyContactPhone = values.emergencyContactPhone.replace(/[-,(,)]/g, "");
            if (courierPhoto) {
                await courierPhoto.toBlob(async blob => {
                    const resultOfRequest = await stores.courierStore.uploadImage(blob);
                    values.photo = resultOfRequest.imageId;
                    await handleCourierSubmit(values);
                });
            } else {
                if (values.photoId) {
                    values.photo = values.photoId;
                }
                await handleCourierSubmit(values);
            }
        }
    });

    const {values, errors, touched, handleChange, setFieldValue} = formik;

    const handleCourierSubmit = async (values: newCourierFormPropType) => {
        if (courierId) {
            const result = await stores.courierStore.updateCourier({courier: values});
            await setLoadingCourier(false);
            if (result) {
                history.push("/couriers/" + courierId);
            }
        } else {
            values.status = "unavailable";
            const result = await stores.courierStore.createCourier(values);
            await setLoadingCourier(false);
            if (result) {
                history.push("/couriers/" + result._id);
            }
        }
    };

    const geolocateCourierHomeLocation = async e => {
        setGeolocationLoading(true);

        const result = await stores.courierStore.getCourierHomeLocation({
            address: formik.values.address
        });

        if (result) {
            const coords = {
                longitude: result?.lon || 0,
                latitude: result?.lat || 0
            };
            formik.setFieldValue("homeLocation", coords);
        }
        setGeolocationLoading(false);
    };

    const checkCourierDedicationRequiredFields = ({courier = {}, companyId = ""}) => {
        const company = stores.appStore?.companyListFilter?.find(
            company => company?._id?.toString() === companyId?.toString()
        );
        if (!company) {
            return true;
        }

        const courierDedicationRequiredFields = company?.courierDedicationRequiredFields || [];
        if (!courierDedicationRequiredFields || courierDedicationRequiredFields.length === 0) {
            return true;
        }

        const missingFields = [];

        courierDedicationRequiredFields.forEach(field => {
            if (
                courier?.[field] === undefined ||
                courier?.[field] === null ||
                courier?.[field] === ""
            ) {
                missingFields.push(t(`fields.${field}`, {ns: "common"}));
            }
        });
        if (missingFields.length > 0) {
            const companyName = company?.companyName || "";
            stores.appStore.openErrorModal({
                content: `${companyName} firmasına bağlı kuryeler için ${missingFields.join(
                    ", "
                )} zorunludur.`,
                onOk: () => {
                    return false;
                }
            });
            return false;
        }

        return true;
    };
    const renderFormInput = (
        textKey: string,
        fieldKey: string,
        flex: number | boolean = 1,
        required = false
    ) => (
        <FormInput
            label={
                i18n?.exists(`fields.${textKey}`, {ns: "common"})
                    ? t(`fields.${textKey}`, {ns: "common"})
                    : textKey
            }
            fieldKey={fieldKey}
            value={values[fieldKey]}
            error={errors[fieldKey]}
            touched={touched[fieldKey]}
            onChange={handleChange}
            flex={flex}
            required={required}
        />
    );

    const renderFormSelect = (
        textKey: string,
        fieldKey: string,
        data: any[],
        mapKey = null,
        mapValue = null,
        disabled,
        flex: number | boolean = 1,
        textFromi18n: boolean = false,
        required = false
    ) => (
        <FormSelect
            label={t(`fields.${textKey}`, {ns: "common"})}
            fieldKey={fieldKey}
            value={values[fieldKey]}
            error={errors[fieldKey]}
            touched={touched[fieldKey]}
            onChange={value => setFieldValue(fieldKey, value)}
            data={data}
            mapKey={mapKey}
            mapValue={mapValue}
            disabled={disabled}
            flex={flex}
            textFromi18n={textFromi18n}
            required={required}
            t={t}
        />
    );

    const renderMaskedInput = (
        textKey: string,
        fieldKey: string,
        flex: number | boolean = 1,
        required = false
    ) => (
        <MaskedInputField
            label={t(`fields.${textKey}`, {ns: "common"})}
            fieldKey={fieldKey}
            value={values[fieldKey]}
            error={errors[fieldKey]}
            touched={touched[fieldKey]}
            onChange={handleChange}
            flex={flex}
            required={required}
        />
    );

    const renderTextArea = (
        textKey: string,
        fieldKey: string,
        placeholder: string,
        required = true
    ) => (
        <TextAreaField
            label={t(`fields.${textKey}`, {ns: "common"})}
            fieldKey={fieldKey}
            value={values[fieldKey]}
            error={errors[fieldKey]}
            touched={touched[fieldKey]}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
        />
    );

    const renderCheckbox = (value: Boolean, setValue: () => void, textKey: string) => (
        <FormCheckbox
            value={value}
            onChange={setValue}
            label={t(textKey, {ns: "newCourier"})}
            textKey={textKey}
        />
    );

    const renderDatePicketField = (textKey: string, fieldKey: string, required = false) => (
        <FormDatePicker
            label={t(`fields.${textKey}`, {ns: "common"})}
            fieldKey={fieldKey}
            value={values[fieldKey]}
            error={errors[fieldKey]}
            touched={touched[fieldKey]}
            onChange={date => setFieldValue(fieldKey, date)}
            required={required}
        />
    );

    const renderSwitch = (textKey: string, fieldKey: string, flex: number | boolean = 1) => (
        <FormSwitch
            label={t(`fields.${textKey}`, {ns: "common"})}
            fieldKey={fieldKey}
            value={values[fieldKey]}
            onChange={checked => setFieldValue(fieldKey, checked)}
            flex={flex}
        />
    );

    const renderLicensePlateInput = (
        textKey: string,
        fieldKey: string,
        flex: number | boolean = 1,
        required = false
    ) => (
        <FormInput
            label={
                i18n?.exists(`fields.${textKey}`, {ns: "common"})
                    ? t(`fields.${textKey}`, {ns: "common"})
                    : textKey
            }
            fieldKey={fieldKey}
            value={values[fieldKey]}
            error={errors[fieldKey]}
            touched={touched[fieldKey]}
            onChange={e => {
                const formattedValue = formatLicensePlate(e.target.value);
                setFieldValue(fieldKey, formattedValue);
            }}
            flex={flex}
            required={required}
        />
    );

    return {
        formik,
        geolocateCourierHomeLocation,
        renderFormInput,
        renderFormSelect,
        renderMaskedInput,
        renderTextArea,
        renderCheckbox,
        renderDatePicketField,
        renderSwitch,
        renderLicensePlateInput,
        setCourierPhoto,
        setGeolocationLoading,
        setMapZoom,
        setMapViewCenter,
        mapZoom,
        mapViewCenter,
        geolocationLoading,
        courierPhoto
    };
};
